import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React from "react";
import { Toaster } from "react-hot-toast";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

// Auth
import { BlacklistProvider } from "./context/BlacklistContext";
import { IpBlacklistProvider } from "./context/IpBlacklistContext";
import { AuthProvider, useAuth } from "./hooks/useAuth";

// Layout
import { AdminLayout } from "./components/layout/AdminLayout";

// Pages
import { CustomerDetailsPage } from "./pages/customers/CustomerDetailsPage";
import { CustomersPage } from "./pages/customers/CustomersPage";
import { DashboardPage } from "./pages/DashboardPage";
import { InvoicesPage } from "./pages/invoices/InvoicesPage";
import { LoginPage } from "./pages/LoginPage";
import { MonitoringPage } from "./pages/monitoring/MonitoringPage";
import { OrderDetailsPage, OrdersPage } from "./pages/orders";
import { PaymentsPage } from "./pages/payments";
import { ProductDetailsPage } from "./pages/products/ProductDetailsPage";
import { ProductsPage } from "./pages/products/ProductsPage";
import { ReportsPage } from "./pages/reports/ReportsPage";
import { SettingsPage } from "./pages/settings/SettingsPage";
import { ShippingPage } from "./pages/shipping/ShippingPage";

// Common Components
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import { LoadingScreen } from "./components/common/LoadingScreen";
import { BlacklistPage } from "./pages/blacklist/BlacklistPage";
import { DraftsPage } from "./pages/drafts";
import { ReturnsPage } from "./pages/returns";

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: 1,
      staleTime: 30 * 1000, // 30 seconds
      refetchOnMount: true,
      gcTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { state } = useAuth();
  const location = useLocation();

  if (state.loading) {
    return <LoadingScreen fullScreen />;
  }

  if (!state.isAuthenticated) {
    return (
      <Navigate
        to={`/login/?redirect=${encodeURIComponent(
          location.pathname + location.search + location.hash
        )}`}
        replace
      />
    );
  }

  return <ErrorBoundary>{children}</ErrorBoundary>;
}

// Redirect component for dynamic paths
function RedirectWithId() {
  const { pathname } = useLocation();
  const id = pathname.split("/").pop();
  const basePath = pathname.split("/").slice(0, -1).join("/");
  return <Navigate to={`${basePath}/${id}/`} replace />;
}

function AdminApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BlacklistProvider>
          <IpBlacklistProvider>
            <BrowserRouter>
              <React.Suspense fallback={<LoadingScreen fullScreen />}>
                <Routes>
                  {/* Redirect routes for URLs without trailing slash */}
                  <Route path="" element={<Navigate to="/" replace />} />
                  <Route
                    path="login"
                    element={<Navigate to="/login/" replace />}
                  />
                  <Route
                    path="orders"
                    element={<Navigate to="/orders/" replace />}
                  />
                  <Route path="orders/:id" element={<RedirectWithId />} />
                  <Route
                    path="shipping"
                    element={<Navigate to="/shipping/" replace />}
                  />
                  <Route
                    path="invoices"
                    element={<Navigate to="/invoices/" replace />}
                  />
                  <Route
                    path="customers"
                    element={<Navigate to="/customers/" replace />}
                  />
                  <Route path="customers/:id" element={<RedirectWithId />} />
                  <Route
                    path="products"
                    element={<Navigate to="/products/" replace />}
                  />
                  <Route path="products/:id" element={<RedirectWithId />} />
                  <Route
                    path="payments"
                    element={<Navigate to="/payments/" replace />}
                  />
                  <Route
                    path="reports"
                    element={<Navigate to="/reports/" replace />}
                  />
                  <Route
                    path="monitoring"
                    element={<Navigate to="/monitoring/" replace />}
                  />
                  <Route
                    path="settings"
                    element={<Navigate to="/settings/" replace />}
                  />
                  <Route
                    path="blacklist"
                    element={<Navigate to="/blacklist/" replace />}
                  />
                  <Route
                    path="drafts"
                    element={<Navigate to="/drafts/" replace />}
                  />

                  {/* Main routes with trailing slashes */}
                  <Route path="login/" element={<LoginPage />} />

                  {/* Protected Routes */}
                  <Route
                    path="/"
                    element={
                      <PrivateRoute>
                        <AdminLayout />
                      </PrivateRoute>
                    }
                  >
                    <Route index element={<DashboardPage />} />
                    <Route path="orders/" element={<OrdersPage />} />
                    <Route path="orders/:id/" element={<OrderDetailsPage />} />
                    <Route path="shipping/" element={<ShippingPage />} />
                    <Route path="invoices/" element={<InvoicesPage />} />
                    <Route path="customers/" element={<CustomersPage />} />
                    <Route path="blacklist/" element={<BlacklistPage />} />
                    <Route path="drafts/" element={<DraftsPage />} />
                    <Route path="returns/" element={<ReturnsPage />} />
                    <Route
                      path="customers/:id/"
                      element={<CustomerDetailsPage />}
                    />
                    <Route path="products/" element={<ProductsPage />} />
                    <Route
                      path="products/:id/"
                      element={<ProductDetailsPage />}
                    />
                    <Route path="payments/" element={<PaymentsPage />} />
                    <Route path="reports/" element={<ReportsPage />} />
                    <Route path="monitoring/" element={<MonitoringPage />} />
                    <Route path="settings/" element={<SettingsPage />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Route>
                </Routes>
              </React.Suspense>
            </BrowserRouter>
          </IpBlacklistProvider>
        </BlacklistProvider>
      </AuthProvider>

      <ReactQueryDevtools initialIsOpen={false} />
      <Toaster position="top-right" />
    </QueryClientProvider>
  );
}

export default AdminApp;
